import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Video, CheckCircle, Music, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// This is a simplified join page that ONLY handles camera + name + join
// Uses exact same video code as CameraTest which works
const SimpleJoin = () => {
  const { roomCode: urlRoomCode } = useParams();
  const navigate = useNavigate();
  
  const [roomCode] = useState(urlRoomCode || 'CASA-MIX-7842');
  const [name, setName] = useState('');
  const [stream, setStream] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [joining, setJoining] = useState(false);
  const videoRef = useRef(null);

  // EXACT copy from CameraTest
  useEffect(() => {
    if (stream && videoRef.current) {
      const video = videoRef.current;
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
      video.muted = true;
      
      const playVideo = async () => {
        try {
          await video.play();
          setVideoPlaying(true);
        } catch (err) {
          console.log('Play failed:', err);
        }
      };
      
      playVideo();
      video.onloadedmetadata = () => playVideo();
    }
  }, [stream]);

  const enableCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: true
      });
      setStream(mediaStream);
      toast.success('Camera enabled!');
    } catch (err) {
      toast.error('Camera error: ' + err.message);
    }
  };

  const forcePlay = async () => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      try {
        await videoRef.current.play();
        setVideoPlaying(true);
      } catch (e) {}
    }
  };

  const joinSession = async () => {
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!stream) {
      toast.error('Please enable camera first');
      return;
    }
    
    setJoining(true);
    
    // Store stream in window for the main join page to use
    window.__cameraStream = stream;
    
    // Navigate to the main session page
    navigate(`/join-session/${roomCode}?name=${encodeURIComponent(name)}&ready=true`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Music className="w-10 h-10 text-amber-400" />
          <h1 className="text-3xl font-bold">Escola de Samba Casa Samba</h1>
        </div>
        
        <div className="bg-gray-800 rounded-2xl p-6 shadow-2xl">
          <div className="bg-amber-500/20 text-amber-400 rounded-lg py-2 px-4 mb-4 text-center">
            Room: {roomCode}
          </div>
          
          {/* Video Preview - EXACT same structure as CameraTest */}
          <div className="w-full h-48 bg-black rounded-xl overflow-hidden mb-4 relative border-4 border-amber-500">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)', backgroundColor: '#000' }}
            />
            
            {!stream ? (
              <button
                onClick={enableCamera}
                className="absolute inset-0 flex flex-col items-center justify-center bg-black/90"
              >
                <Video className="w-16 h-16 text-amber-400 mb-3" />
                <p className="text-amber-400 font-bold text-lg">TAP TO ENABLE CAMERA</p>
              </button>
            ) : !videoPlaying ? (
              <div 
                onClick={forcePlay}
                className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 cursor-pointer"
              >
                <Video className="w-12 h-12 text-amber-400 mb-2" />
                <p className="text-amber-400 font-bold">TAP TO SEE VIDEO</p>
              </div>
            ) : (
              <div className="absolute bottom-2 left-2 bg-green-500 px-2 py-1 rounded text-xs text-black font-bold flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Camera Ready
              </div>
            )}
          </div>
          
          {/* Name Input */}
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full bg-gray-900 border-2 border-gray-700 rounded-xl px-4 py-4 text-xl text-center focus:outline-none focus:border-green-500 mb-4"
          />
          
          {/* Join Button */}
          <button
            onClick={joinSession}
            disabled={!stream || !name.trim() || joining}
            className={`w-full font-bold text-xl py-5 rounded-xl transition-colors flex items-center justify-center gap-3 ${
              stream && name.trim() && !joining
                ? 'bg-green-500 hover:bg-green-600 text-black'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            {joining ? 'Joining...' : (
              <>
                JOIN SESSION
                <ArrowRight className="w-6 h-6" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleJoin;
